/**
 * <protector-fit-finder>
 *
 * Single jacket dropdown. When a jacket is selected, the matching back
 * protector's title is shown below with a link to the protector's PDP.
 *
 * Jacket availability is intentionally ignored: the jacket is only an
 * input for the size lookup — the customer is buying the protector, not
 * the jacket. Every paired jacket is therefore always selectable.
 *
 * Also intercepts the main product form's submit when a jacket is picked:
 * the cart-add routes to the matched protector variant instead of the
 * page's current product. When no jacket is picked, the form submits
 * normally and adds the page's current product.
 *
 * Data shape (embedded as <script type="application/json"
 * data-fit-finder-data>):
 *   {
 *     "<jacket_variant_id>": {
 *       jacket_label, jacket_url,
 *       protector_title, protector_variant_label, protector_url,
 *       protector_variant_id, available
 *     },
 *     ...
 *   }
 */
if (!customElements.get('protector-fit-finder')) {
  // Separator used by the Liquid block when it appends the variant title
  // to the product title (`j_short | append: ' — ' | append: j_suffix`).
  const LABEL_SEPARATOR = ' — ';

  // Canonical smallest-to-largest order. Shorthand forms like "3XL" and
  // "2XS" are normalised into this scheme before lookup.
  const SIZE_ORDER = [
    'XXXXS', 'XXXS', 'XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'XXXXL',
  ];

  function normalizeSize(raw) {
    let s = String(raw || '').trim().toUpperCase();
    if (!s) return '';
    if (s.includes('/')) s = s.split('/').pop().trim();
    const shorthand = s.match(/^([2-9])X([SL])$/);
    if (shorthand) s = 'X'.repeat(Number(shorthand[1])) + shorthand[2];
    return s;
  }

  function sizeRank(raw) {
    const s = normalizeSize(raw);
    const known = SIZE_ORDER.indexOf(s);
    if (known !== -1) return known;
    const numeric = parseFloat(s);
    if (!Number.isNaN(numeric)) return 1000 + numeric;
    return 9000;
  }

  function splitLabel(label) {
    const l = String(label || '');
    const i = l.lastIndexOf(LABEL_SEPARATOR);
    if (i === -1) return { title: l, size: '' };
    return { title: l.slice(0, i), size: l.slice(i + LABEL_SEPARATOR.length) };
  }

  class ProtectorFitFinder extends HTMLElement {
    connectedCallback() {
      try {
        const dataEl = this.querySelector('[data-fit-finder-data]');
        this.data = dataEl ? JSON.parse(dataEl.textContent) : {};

        this.input = this.querySelector('[data-fit-finder-jacket]');
        this.list = this.querySelector('[data-fit-finder-list]');
        this.combobox = this.querySelector('[data-fit-finder-combobox]');
        this.result = this.querySelector('[data-fit-finder-result]');
        this.resultTitle = this.querySelector('[data-fit-finder-result-title]');
        this.resultVariant = this.querySelector('[data-fit-finder-result-variant]');
        this.variantInput = this.querySelector('[data-fit-finder-variant-input]');

        this.form = this.dataset.productFormId
          ? document.getElementById(this.dataset.productFormId)
          : null;

        if (!this.input || !this.list) return;

        this._entries = this._buildEntries();
        this._selectedId = null;
        this._activeIndex = -1;
        this._renderList(this._entries);

        this._wireComboboxEvents();
        this._wireFormInterception();
      } catch (err) {
        console.warn('[protector-fit-finder] init failed', err);
      }
    }

    _buildEntries() {
      const entries = Object.keys(this.data).map((id) => {
        const entry = { id, ...this.data[id] };
        const parsed = splitLabel(entry.jacket_label);
        const size = entry.jacket_size != null && entry.jacket_size !== ''
          ? entry.jacket_size
          : parsed.size;
        entry._sortTitle = parsed.title;
        entry._sortSizeRank = sizeRank(size);
        entry._sortSize = String(size || '');
        return entry;
      });

      return entries.sort((a, b) => {
        const byTitle = a._sortTitle.localeCompare(b._sortTitle, undefined, {
          sensitivity: 'base',
          numeric: true,
        });
        if (byTitle !== 0) return byTitle;
        if (a._sortSizeRank !== b._sortSizeRank) {
          return a._sortSizeRank - b._sortSizeRank;
        }
        return a._sortSize.localeCompare(b._sortSize, undefined, {
          sensitivity: 'base',
          numeric: true,
        });
      });
    }

    _filter(query) {
      const q = (query || '').trim().toLowerCase();
      if (!q) return this._entries;
      return this._entries.filter((e) => (e.jacket_label || '').toLowerCase().includes(q));
    }

    _renderList(entries) {
      this.list.innerHTML = '';
      this._visibleEntries = entries;
      entries.forEach((entry) => {
        const li = document.createElement('li');
        li.className = 'protector-fit-finder__option';
        li.setAttribute('role', 'option');
        li.dataset.entryId = entry.id;
        li.textContent = entry.jacket_label;
        if (this._selectedId && this._selectedId === entry.id) {
          li.setAttribute('aria-selected', 'true');
          li.classList.add('is-selected');
        }
        this.list.appendChild(li);
      });
      if (!entries.length) {
        const li = document.createElement('li');
        li.className = 'protector-fit-finder__option protector-fit-finder__option--empty';
        li.textContent = 'No matches';
        li.setAttribute('aria-disabled', 'true');
        this.list.appendChild(li);
      }
    }

    _openList() {
      if (!this.list.hidden) return;
      this.list.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    _closeList() {
      if (this.list.hidden) return;
      this.list.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this._activeIndex = -1;
      this._highlightActive();
    }

    _highlightActive() {
      const items = Array.from(this.list.querySelectorAll('.protector-fit-finder__option'));
      items.forEach((el, i) => {
        el.classList.toggle('is-active', i === this._activeIndex);
      });
      const active = items[this._activeIndex];
      if (active) active.scrollIntoView({ block: 'nearest' });
    }

    _moveActive(delta) {
      const items = this._visibleEntries || [];
      if (!items.length) return;
      let next = this._activeIndex + delta;
      if (next < 0) next = items.length - 1;
      if (next >= items.length) next = 0;
      this._activeIndex = next;
      this._highlightActive();
    }

    _selectEntry(entry) {
      if (!entry) return;
      this._selectedId = entry.id;
      this.input.value = entry.jacket_label || '';
      this._closeList();
      this._onJacketChange();
    }

    _wireComboboxEvents() {
      // Open on focus / click — show full list.
      this.input.addEventListener('focus', () => {
        this._renderList(this._filter(this.input.value));
        this._openList();
      });
      this.input.addEventListener('click', () => {
        this._renderList(this._filter(this.input.value));
        this._openList();
      });

      // Filter on input.
      this.input.addEventListener('input', () => {
        this._activeIndex = -1;
        this._renderList(this._filter(this.input.value));
        this._openList();
        // Clearing the input clears the selection.
        if (!this.input.value.trim()) {
          this._selectedId = null;
          this._onJacketChange();
        }
      });

      // Keyboard navigation.
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (this.list.hidden) {
            this._renderList(this._filter(this.input.value));
            this._openList();
          }
          this._moveActive(1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          this._moveActive(-1);
        } else if (e.key === 'Enter') {
          if (!this.list.hidden && this._activeIndex >= 0) {
            e.preventDefault();
            this._selectEntry(this._visibleEntries[this._activeIndex]);
          }
        } else if (e.key === 'Escape') {
          this._closeList();
        }
      });

      // Click on an option.
      this.list.addEventListener('mousedown', (e) => {
        const li = e.target.closest('.protector-fit-finder__option');
        if (!li || li.getAttribute('aria-disabled') === 'true') return;
        e.preventDefault(); // keep focus on input
        const id = li.dataset.entryId;
        const entry = this._visibleEntries.find((x) => x.id === id);
        this._selectEntry(entry);
      });

      // Hover to set active.
      this.list.addEventListener('mousemove', (e) => {
        const li = e.target.closest('.protector-fit-finder__option');
        if (!li) return;
        const id = li.dataset.entryId;
        const idx = this._visibleEntries.findIndex((x) => x.id === id);
        if (idx !== -1 && idx !== this._activeIndex) {
          this._activeIndex = idx;
          this._highlightActive();
        }
      });

      // Close on outside click / blur.
      document.addEventListener('mousedown', (e) => {
        if (!this.combobox.contains(e.target)) this._closeList();
      });
    }

    _onJacketChange() {
      const id = this._selectedId;
      const info = id ? this.data[id] : null;

      if (!info) {
        if (this.result) this.result.hidden = true;
        if (this.resultTitle) this.resultTitle.textContent = '';
        if (this.resultVariant) this.resultVariant.textContent = '';
        if (this.variantInput) this.variantInput.value = '';
        this._currentProtectorVariantId = null;
        return;
      }

      // When the matched protector IS the current product, drive the page's
      // own variant picker so the size flips in the existing UI. We still
      // populate the result line so the customer sees the confirmation.
      // Disabled via block setting `auto_select_variant`.
      const autoSelectEnabled = this.dataset.autoSelectVariant !== 'false';
      if (autoSelectEnabled && info.is_current_product) {
        this._selectCurrentProductVariant(info);
        if (this.variantInput) this.variantInput.value = '';
        this._currentProtectorVariantId = null;
      } else {
        const protectorVariantId = info.protector_variant_id || '';
        if (this.variantInput) this.variantInput.value = String(protectorVariantId || '');
        this._currentProtectorVariantId = protectorVariantId || null;
      }

      if (this.resultTitle) this.resultTitle.textContent = info.protector_title || '';
      if (this.resultVariant) this.resultVariant.textContent = info.protector_variant_label || '';
      if (this.result) this.result.hidden = false;
    }

    /**
     * Selects the matched variant in the page's <variant-selects> picker by
     * setting each fieldset's radio/select to the variant's option values,
     * then dispatching `change` so the theme runs its normal variant-change
     * pipeline (price, image, master id, URL all update). Returns true if
     * the picker was found and updated.
     */
    _selectCurrentProductVariant(info) {
      const picker = document.querySelector('variant-selects');
      if (!picker) return false;
      const options = Array.isArray(info.protector_variant_options) ? info.protector_variant_options : [];
      const fieldsets = Array.from(picker.querySelectorAll('fieldset'));
      if (!fieldsets.length) return false;

      let updated = false;
      fieldsets.forEach((fs, i) => {
        const value = options[i];
        if (value == null || value === '') return;
        const select = fs.querySelector('select');
        if (select) {
          const opt = Array.from(select.options).find((o) => o.value === value);
          if (opt && select.value !== value) {
            select.value = value;
            updated = true;
          }
          return;
        }
        const radio = fs.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
        if (radio && !radio.checked) {
          radio.checked = true;
          updated = true;
        }
      });

      if (updated) picker.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    _wireFormInterception() {
      if (!this.form || this._formAttached) return;
      this._formAttached = true;
      // Capture phase so we preempt other submit handlers (e.g. <product-form>)
      // and stop their single-item add from firing when we want to route the
      // request to the matched protector variant instead.
      this.form.addEventListener('submit', this._onFormSubmit.bind(this), { capture: true });
    }

    async _onFormSubmit(event) {
      const variantId = this._currentProtectorVariantId;
      if (!variantId) return; // no jacket selected → let normal handler run

      event.preventDefault();
      event.stopImmediatePropagation();

      const sections = ['cart-drawer', 'cart-bubble', 'main-cart'];
      const items = [
        {
          id: Number(variantId),
          quantity: 1,
          properties: { _added_via: 'fit_finder' },
        },
      ];

      this._setSubmittingState(true);

      try {
        const addUrl = (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add';
        const response = await fetch(`${addUrl}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
        });
        const data = await response.json();
        if (data.status && data.status >= 400) {
          console.warn('[protector-fit-finder] cart/add failed', data);
          this._setSubmittingState(false);
          return;
        }
        this._renderCartContents(data);
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: (data.items && data.items[0]) || data } }));
      } catch (err) {
        console.error('[protector-fit-finder] cart add error', err);
      } finally {
        this._setSubmittingState(false);
      }
    }

    _setSubmittingState(isSubmitting) {
      const btns = document.querySelectorAll('.single-add-to-cart-button');
      btns.forEach((btn) => {
        btn.classList.toggle('loading', isSubmitting);
        btn.toggleAttribute('aria-disabled', isSubmitting);
      });
    }

    _renderCartContents(parsedState) {
      const sections = parsedState.sections || {};
      const targets = [
        { id: 'Cart', selector: '.thb-cart-form', section: 'main-cart' },
        { id: 'Cart-Drawer', selector: '.cart-drawer', section: 'cart-drawer' },
        { id: 'cart-drawer-toggle', selector: '#cart-drawer-toggle', section: 'cart-bubble' },
      ];
      targets.forEach((t) => {
        const host = document.getElementById(t.id);
        if (!host || !sections[t.section]) return;
        const replace = host.querySelector(t.selector) || host;
        try {
          const html = new DOMParser().parseFromString(sections[t.section], 'text/html');
          const inner = html.querySelector(t.selector);
          if (inner) replace.innerHTML = inner.innerHTML;
        } catch (e) {
          console.warn('[protector-fit-finder] failed to render', t.section, e);
        }
      });
      const drawer = document.getElementById('Cart-Drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
      drawer?.notesToggle?.();
      drawer?.removeProductEvent?.();
    }
  }

  customElements.define('protector-fit-finder', ProtectorFitFinder);
}